import React from 'react';
import './Home.css';
import Footer from 'shared/components/footer/Footer';
import {
  ButtonLink,
  FunnelPage,
  FunnelSection,
  SectionHeader,
} from 'shared/components/funnel/FunnelUI';

const marketRows = [
  { pair: 'BTC plan', side: 'Levels', status: 'Sweep > reclaim', value: '4H' },
  { pair: 'ETH watch', side: 'Trigger', status: 'Hold reclaim only', value: '1D' },
  { pair: 'SOL note', side: 'Risk', status: 'Invalidation first', value: 'Live' },
];

const channelStats = [
  { value: 'Free', label: 'Telegram entry' },
  { value: 'Daily', label: 'Market context' },
  { value: '4', label: 'Upgrade paths' },
];

const channelFeatures = [
  {
    index: '01',
    title: 'Market context first',
    copy: 'Get short updates, directional notes, and the reasoning behind the trade ideas before choosing a paid product.',
  },
  {
    index: '02',
    title: 'Setup context kept visible',
    copy: 'Charts, levels, and invalidation notes stay connected so you can follow the thinking without guessing.',
  },
  {
    index: '03',
    title: 'Paid paths explained',
    copy: 'VIP, the AI indicator, copy trading, and portfolio access are introduced only after the route makes sense.',
  },
  {
    index: '04',
    title: 'Fast next step',
    copy: 'Join the free channel, watch the updates, and move when the fit is clear. No account setup is needed to start.',
  },
];

const serviceCards = [
  {
    eyebrow: 'Start here',
    title: 'Free Telegram',
    copy: 'The default entry point for market context, proof previews, and product explanations.',
    className: 'home-path-card--primary',
  },
  {
    eyebrow: 'Signals',
    title: 'VIP resources',
    copy: 'Structured signal context, market notes, and educational discussion when you want a tighter stream.',
  },
  {
    eyebrow: 'Charts',
    title: 'AI indicator',
    copy: 'A technical-analysis tool for trend, timing, and confirmation inside your own process.',
  },
  {
    eyebrow: 'Account access',
    title: 'Copy trading',
    copy: 'Blofin signup, UID verification, Discord ticket submission, and access once requirements are met.',
  },
];

const liveNotes = [
  {
    label: 'Market note',
    title: 'BTC reclaim level marked',
    copy: 'Chart zone, trigger, and invalidation shared before any entry talk.',
  },
  {
    label: 'Signal context',
    title: 'Why the setup matters',
    copy: 'The free channel shows the reasoning first, not just a rushed direction.',
  },
  {
    label: 'Next step',
    title: 'VIP path only if it fits',
    copy: 'Paid products are introduced after you have seen the style and pace.',
  },
];

function Home() {
  return (
    <FunnelPage className="home-page">
      <FunnelSection className="home-hero" tone="hero">
        <div className="home-market-visual" aria-hidden="true">
          <div className="home-market-header">
            <span>Telegram desk</span>
            <strong>Live notes</strong>
          </div>
          {marketRows.map((row) => (
            <div className="home-market-row" key={row.pair}>
              <span>{row.pair}</span>
              <strong>{row.side}</strong>
              <em>{row.status}</em>
              <b>{row.value}</b>
            </div>
          ))}
          <div className="home-market-footer">
            <span>Chart posted</span>
            <span>Entry trigger</span>
            <span>Risk first</span>
          </div>
        </div>

        <div className="home-hero-content">
          <p className="funnel-eyebrow">Market context, proof previews, and next steps</p>
          <h1>CRYPTOXNFT Free Telegram</h1>
          <p className="home-hero-lede">
            Join the free channel first. Watch the market updates, understand the style, then move into VIP, the AI indicator, copy trading, or portfolio access only when it matches how you trade.
          </p>
          <div className="home-hero-actions">
            <ButtonLink href="https://t.me/cryptoxnfts">Join Free Telegram</ButtonLink>
            <ButtonLink to="/services" variant="secondary">See What Is Inside</ButtonLink>
          </div>
          <div className="home-hero-stats" aria-label="CRYPTOXNFT channel highlights">
            {channelStats.map((stat) => (
              <div className="home-stat" key={stat.label}>
                <strong>{stat.value}</strong>
                <span>{stat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </FunnelSection>

      <FunnelSection className="home-live-section">
        <div className="home-live-layout">
          <div className="home-live-copy">
            <p className="funnel-eyebrow">Telegram preview</p>
            <h2>See how the free channel thinks before you buy anything.</h2>
            <p>
              The first experience is not a product wall. It is a live stream of levels, context, risk notes, and the occasional upgrade path when it actually makes sense.
            </p>
            <ButtonLink href="https://t.me/cryptoxnfts">Join Free Telegram</ButtonLink>
          </div>
          <div className="home-live-stage" aria-label="Telegram channel preview">
            <div className="home-live-radar" aria-hidden="true">
              <span />
              <span />
              <span />
            </div>
            <div className="home-live-feed">
              {liveNotes.map((note) => (
                <article className="home-live-note" key={note.title}>
                  <span>{note.label}</span>
                  <h3>{note.title}</h3>
                  <p>{note.copy}</p>
                </article>
              ))}
            </div>
          </div>
        </div>
      </FunnelSection>

      <FunnelSection className="home-channel-section" tone="muted">
        <div className="home-channel-layout">
          <div className="home-channel-copy">
            <p className="funnel-eyebrow">Inside the free channel</p>
            <h2>Start free. Upgrade later.</h2>
            <p>
              Join Telegram first. Watch the levels, risk notes, and context. Move only if it fits.
            </p>
            <ButtonLink href="https://t.me/cryptoxnfts">Join Free Telegram</ButtonLink>
          </div>
          <div className="home-channel-grid">
            {channelFeatures.map((feature) => (
              <article className="home-channel-item" key={feature.title}>
                <span>{feature.index}</span>
                <h3>{feature.title}</h3>
                <p>{feature.copy}</p>
              </article>
            ))}
          </div>
        </div>
      </FunnelSection>

      <FunnelSection className="home-routes-section">
        <SectionHeader
          eyebrow="After Telegram"
          title="Move into the paid path only when the fit is clear."
          copy="The cards show what can happen next, but the call to action stays simple: start free, learn the CRYPTOXNFT style, and upgrade with context."
        />
        <div className="home-path-grid">
          {serviceCards.map((card) => (
            <article
              key={card.title}
              className={`home-path-card ${card.className || ''}`.trim()}
            >
              <p>{card.eyebrow}</p>
              <h3>{card.title}</h3>
              <span>{card.copy}</span>
            </article>
          ))}
        </div>
        <div className="home-routes-actions">
          <ButtonLink href="https://t.me/cryptoxnfts">Join Free Telegram</ButtonLink>
          <ButtonLink to="/services" variant="secondary">See What Is Inside</ButtonLink>
        </div>
      </FunnelSection>

      <FunnelSection className="home-final-section" tone="panel">
        <div className="home-final-panel">
          <div>
            <p className="funnel-eyebrow">Final choice</p>
            <h2>Start with the channel. Decide with context.</h2>
            <p>
              Telegram is the entry point for updates, proof previews, and product context. VIP, the indicator, copy trading, and portfolio access come after you know what fits.
            </p>
          </div>
          <div className="home-final-actions">
            <ButtonLink href="https://t.me/cryptoxnfts">Free Telegram</ButtonLink>
            <ButtonLink to="/services/vip" variant="secondary">VIP</ButtonLink>
            <ButtonLink to="/services/indicator" variant="secondary">Indicator</ButtonLink>
            <ButtonLink to="/services/copy-trading" variant="secondary">Copy Trading</ButtonLink>
          </div>
        </div>
      </FunnelSection>

      <Footer />
    </FunnelPage>
  );
}

export default Home;
